import { FC, MouseEvent, useEffect, useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';

import { logger } from '@azure/storage-blob';
import { Box, Button, Divider, Menu, Stack, styled, Grid } from '@mui/material';

import { MarkerTypeApi, UserApi } from '@/api';
import { IcFilter } from '@/assets/images';
import {
  NarrowButton,
  RenderFormField,
  ActiveFilterIndicator,
} from '@/components';
import { Role } from '@/models';

export interface ILocationMarkerFilter {
  from: string;
  to: string;
  createdBy: string;
  markerName: string;
  hasExpiration: boolean | null;
}

interface IProps {
  onChangeFilter: (value: ILocationMarkerFilter) => void;
  filterValues: ILocationMarkerFilter;
}

const FormRoot = styled('form')(() => ({
  padding: 16,
  minWidth: 320,
  '& .divider': {
    marginBottom: 16,
  },
}));

const initialFilterValue: ILocationMarkerFilter = {
  createdBy: '',
  markerName: '',
  from: '',
  to: '',
  hasExpiration: null,
};

export const LocationMarkerFilterMenu: FC<IProps> = ({
  onChangeFilter,
  filterValues,
}) => {
  const methods = useForm<ILocationMarkerFilter>({
    defaultValues: initialFilterValue,
    mode: 'all',
  });

  const { handleSubmit, reset } = methods;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showFilterDot, setShowFilterDot] = useState(false);
  const [userOptions, setUserOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [markerNameOptions, setMarkerNameOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const openActionMenu = Boolean(anchorEl);
  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const onClear = () => {
    reset();
    onChangeFilter(initialFilterValue);
    setShowFilterDot(false);
  };

  const onCancel = () => {
    reset();
    handleCloseMenu();
  };

  const onSubmit: SubmitHandler<ILocationMarkerFilter> = (data) => {
    onChangeFilter(data);
    const isActive = Object.values(data).some(Boolean);
    setShowFilterDot(isActive);
    handleCloseMenu();
  };

  const filterFields = [
    {
      name: 'createdBy',
      type: 'autocomplete',
      label: 'Created By',
      items: userOptions,
    },
    {
      name: 'markerName',
      type: 'autocomplete',
      label: 'Marker Name',
      items: markerNameOptions,
    },
    {
      name: 'hasExpiration',
      type: 'autocomplete',
      label: 'Has Expiration Date',
      items: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
  ];

  useEffect(() => {
    const supervisorFilter = JSON.stringify({
      'roles.role': {
        $in: [Role.DISPATCHER, Role.ACCOUNT_ADMIN],
      },
    });

    const fetchUsers = async () => {
      try {
        const { data } = await UserApi.get({
          limit: 1000,
          filter: supervisorFilter,
        });
        const options =
          data.results?.map((user: any) => ({
            label: user.fullName,
            value: user.id,
          })) || [];
        setUserOptions(options);
      } catch (err) {
        logger.error('Could not fetch the users');
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    reset(filterValues);
    const isFilterActive = Object.values(filterValues).some(Boolean);
    setShowFilterDot(isFilterActive);
  }, [filterValues, reset]);

  useEffect(() => {
    const fetchMarkerNames = async () => {
      try {
        const { data } = await MarkerTypeApi.getMarkerTypes({
          page: 1,
          limit: 1000,
          sort: 'sortOrder',
        });

        const options =
          data.results?.map((item) => ({
            label: item.name as string,
            value: item.name as string,
          })) || [];

        setMarkerNameOptions(options);
      } catch (err) {
        logger.error('Could not fetch marker names');
      }
    };

    fetchMarkerNames();
  }, []);

  return (
    <>
      <NarrowButton
        variant="contained"
        color="inherit"
        onClick={handleOpenMenu}
        sx={{ position: 'relative', px: 2, py: 1 }}
      >
        <IcFilter />
        {showFilterDot && <ActiveFilterIndicator />}
      </NarrowButton>

      <Menu
        anchorEl={anchorEl}
        open={openActionMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <FormProvider {...methods}>
          <FormRoot onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <RenderFormField name="from" label="From" type="date" />
                  </Grid>
                  <Grid item xs={6}>
                    <RenderFormField name="to" label="To" type="date" />
                  </Grid>
                </Grid>
              </Box>

              {filterFields.map((field) => (
                <RenderFormField key={field.name} {...field} />
              ))}
            </Stack>

            <Divider className="divider" sx={{ marginTop: 2 }} />

            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                variant="contained"
                color="inherit"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button type="button" variant="text" onClick={onClear}>
                Clear
              </Button>
              <Button type="submit" variant="contained" color="error">
                Apply
              </Button>
            </Stack>
          </FormRoot>
        </FormProvider>
      </Menu>
    </>
  );
};
